import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Plus } from 'lucide-react';
import ExpenseForm from '../components/expenses/ExpenseForm';
import ExpenseList from '../components/expenses/ExpenseList';
import ExpenseFilters from '../components/expenses/ExpenseFilters';
import ExpenseStats from '../components/expenses/ExpenseStats';
import Pagination from '../components/common/Pagination';
import { useExpenses } from '../hooks/useExpenses';
import { useProjects } from '../hooks/useProjects';
import { useCategories } from '../hooks/useCategories';
import type { Expense, ExpenseFilter, ExpensePaginationParams } from '../types';

const Expenses: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | undefined>(undefined);
  
  // Estado de filtros
  const [filters, setFilters] = useState<ExpenseFilter>({});
  
  // Estado de paginación
  const [pagination, setPagination] = useState<ExpensePaginationParams>({
    page: 1,
    pageSize: 20,
    sortBy: 'date',
    sortOrder: 'desc',
  });
  
  // Cargar datos
  const { data: expenseData, isLoading } = useExpenses(filters, pagination);
  const { data: projects = [] } = useProjects();
  const { data: categories = [] } = useCategories();
  
  const handleCreateNew = () => {
    setEditingExpense(undefined);
    setShowForm(true);
  };
  
  const handleEdit = (expense: Expense) => {
    setEditingExpense(expense);
    setShowForm(true);
  };
  
  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingExpense(undefined);
  };
  
  const handleFormCancel = () => {
    setShowForm(false);
    setEditingExpense(undefined);
  };
  
  const handleFilterChange = (newFilters: ExpenseFilter) => {
    setFilters(newFilters);
    setPagination({ ...pagination, page: 1 }); // Reset to first page
  };
  
  const handleResetFilters = () => {
    setFilters({});
    setPagination({ ...pagination, page: 1 });
  };
  
  const handlePageChange = (page: number) => {
    setPagination({ ...pagination, page });
  };
  
  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h1 className="text-3xl font-bold text-gray-900">Gastos</h1>
        {!showForm && (
          <Button onClick={handleCreateNew} className="gap-2" disabled={projects.length === 0} title={projects.length === 0 ? 'Debes tener proyectos creados para registrar gastos' : undefined}>
            <Plus className="h-4 w-4" />
            Nuevo Gasto
          </Button>
        )}
      </div>
      
      {showForm ? (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            {editingExpense ? 'Editar Gasto' : 'Nuevo Gasto'}
          </h2>
          <ExpenseForm
            expense={editingExpense}
            onSuccess={handleFormSuccess}
            onCancel={handleFormCancel}
          />
        </div>
      ) : (
        <>
          {/* Filtros */}
          <ExpenseFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onReset={handleResetFilters}
            projects={projects.map(p => ({ projectId: p.projectId, name: p.name }))}
            categories={categories.map(c => ({ categoryId: c.categoryId, name: c.name }))}
          />
          
          {/* Resumen de estadísticas */}
          {expenseData && (
            <ExpenseStats
              totalAmount={expenseData.summary.totalAmount}
              count={expenseData.summary.count}
            />
          )}
          
          {/* Lista de gastos */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {isLoading ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : expenseData && expenseData.items.length > 0 ? (
              <>
                <ExpenseList
                  expenses={expenseData.items}
                  isLoading={isLoading}
                  onEdit={handleEdit}
                />
                
                {/* Paginación */}
                <Pagination
                  currentPage={expenseData.pagination.page}
                  totalPages={expenseData.pagination.totalPages}
                  onPageChange={handlePageChange}
                  pageSize={expenseData.pagination.pageSize}
                  totalItems={expenseData.pagination.total}
                />
              </>
            ) : (
              <div className="text-center py-8 text-gray-500">
                {projects.length === 0
                  ? 'Para crear gastos, primero debes tener proyectos creados.'
                  : 'No hay gastos que coincidan con los filtros'}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Expenses;
